import { CommonModule } from '@angular/common';
import { Component, inject, Inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterModule } from '@angular/router';
import { ApiRequestService, GoogleTagManagerService, Material, ProductPreviewComponent, TranslationPipe } from '@prekog/common-ui';
import { Observable } from 'rxjs';
import { OfferHeaderComponent } from '../shared/components/offerheader/offer-header.component';
import { FindHomeConfigRequest } from '../shared/find-home-config.request';
import { FindHomeConfigResponse } from '../shared/find-home-config.response';
import { HomeConfig } from '../shared/home-config.model';
import { PublicationService } from '../shared/publication.service';
import { SiteConfiguration } from '../shared/site-configuration.model';
import { GetSiteConfigurationByKeyUseCase } from '../use-cases/getsiteconfigurationbykey/get-site-configuration-by-key.usecase';

interface NoveltyProductResponse {
    noveltyProductList?: Material[];
}

@Component({
    selector: 'pf-novelty-products',
    templateUrl: './novelty-products.component.html',
    styleUrls: ['./novelty-products.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
        MatSelectModule,
        MatFormFieldModule,
        TranslationPipe,
        FormsModule,
        RouterModule,
        ProductPreviewComponent,
        OfferHeaderComponent
    ]
})
export class NoveltyProductsComponent implements OnInit {
    configuratorBaseUrl = '';
    filteredPublication: Material[] = [];
    getHomeConfigByComponentUrl = '/websiteconfigurator/find-homeconfig';
    getNoveltyProductsUrl = '/store/getNoveltyProducts';
    homeConfig: HomeConfig = {};
    noveltyProducts: Material[] = [];
    offerLines = signal<string[]>([]);
    offerLinesActive = signal<boolean>(true);

    storeBaseUrl = '';

    private _listFilter = '';

    get listFilter(): string {
        return this._listFilter;
    }

    set listFilter(value: string) {
        this._listFilter = value;
        this.filteredPublication = this.performFilter(value);
    }

    private readonly _getOfferLinesUseCase = inject(GetSiteConfigurationByKeyUseCase);

    constructor(
        private _publicationService: PublicationService,
        public router: Router,
        private _apiRequestService: ApiRequestService,
        @Inject('STORE_BACKEND_URL') private _storeBaseUrl: string,
        private _googleTagManagerService: GoogleTagManagerService,
        @Inject('CONFIGURATOR_BASE_URL') private _configuratorBaseUrl: string
    ) {
        this.storeBaseUrl = _storeBaseUrl;
        this.configuratorBaseUrl = _configuratorBaseUrl;
    }

    ngOnInit(): void {
        this.getProducts();
    }

    getProducts(): void {
        this._apiRequestService
            .sendRequest<{}, NoveltyProductResponse>(this.storeBaseUrl, {}, true, this.getNoveltyProductsUrl)
            .subscribe((response) => {
                this.noveltyProducts = (response.noveltyProductList as Material[]) || [];
                this.noveltyProducts.forEach((product) => {
                    if (product.webshopPriceActive) {
                        product.oldPrice = product.originalPrice;
                        product.originalPrice = product.webshopPrice;
                        if (product.discountLimitedUntil === null) {
                            product.typeOfPrice = 'Kiadói ár:';
                        } else {
                            product.typeOfPrice = 'Kötött ár:';
                        }
                    } else if (product.discountPriceActive) {
                        product.oldPrice = product.originalPrice;
                        product.originalPrice = product.discountPrice;
                        product.typeOfPrice = 'Akciós ár:';
                    } else if (product.hasPreorder) {
                        product.typeOfPrice = 'Előrendelési ár:';
                        product.oldPrice = product.originalPrice;
                        product.originalPrice = product.preorderPrice;
                    } else {
                        if (product.discountLimitedUntil === null) {
                            product.typeOfPrice = 'Ár:';
                        } else {
                            product.typeOfPrice = 'Kötött ár:';
                        }
                    }
                });

                this.filteredPublication = this.noveltyProducts;
                this._googleTagManagerService.viewProductItemList(this.noveltyProducts);
                this.getHomeConfigName();
                this._getOfferLines();
            });
    }

    getHomeConfigName(): void {
        const findHomeConfigRequest: FindHomeConfigRequest = {
            component: 'pf-novelty-products'
        };
        this._apiRequestService
            .sendRequest<
                FindHomeConfigRequest,
                FindHomeConfigResponse
            >(this.configuratorBaseUrl, findHomeConfigRequest, true, this.getHomeConfigByComponentUrl)
            .subscribe((response) => {
                this.homeConfig = response.homeConfig as HomeConfig;
            });
    }

    performFilter(filterBy: string): Material[] {
        filterBy = filterBy.toLocaleLowerCase();
        return this.noveltyProducts.filter(
            (publication: Material) =>
                publication.title?.toLocaleLowerCase().includes(filterBy) || publication.author?.toLocaleLowerCase().includes(filterBy)
        );
    }

    addToCart(material: Material): void {
        if (!sessionStorage.getItem('token') && material.hasPreorder) {
            sessionStorage.setItem('material', JSON.stringify(material));
            this._publicationService.openLogin();
        } else {
            this._publicationService.addToCart(material);
        }
    }

    orderbyAscPrice(): void {
        this.filteredPublication = this.filteredPublication.sort((a, b) => a.originalPrice! - b.originalPrice!);
    }

    orderbyDescPrice(): void {
        this.filteredPublication = this.filteredPublication.sort((a, b) => b.originalPrice! - a.originalPrice!);
    }

    orderbyTitleAbc(): void {
        this.filteredPublication = this.filteredPublication.sort((a, b) => a.title!.localeCompare(b.title!));
    }

    orderByNewest(): void {
        this.filteredPublication = this.filteredPublication.sort((a, b) => b.id! - a.id!);
    }

    private _getOfferLines(): void {
        (this._getOfferLinesUseCase.execute({ configurationKey: 'pf-novelty-products-lines' }) as Observable<SiteConfiguration>).subscribe(
            (config) => {
                this.offerLinesActive.set(config.active ?? true);
                this.offerLines.set(config.configurationValue ? config.configurationValue.split('\n') : []);
            }
        );
    }
}
